import { createSimpleStore } from 'react-simple-reducer'
import api, { IQueryParams } from '../api'
import { ForumModel } from '../models/ForumModel'
import { ForumMensagemModel } from '../models/ForumMensagemModel'

// Forum extends ForumModel with textoPendente
interface Forum extends ForumModel {
  textoPendente: string
}

export interface IRequest {
  errorCode: number
  message: string
  fetching: boolean
}

export interface IForunsState {
  forumActive: Forum
  forumMensagemEditing: Partial<ForumMensagemModel>
  selectedForumMensagem: Partial<ForumMensagemModel>
  foruns: Forum[]
  forunsCount: number
  mensagensPendentes: Forum[]
  mensagensPendentesCount: number
  request: IRequest
  queryForuns: IQueryParams
  queryMensagensPendentes: IQueryParams
}

export const initialState: IForunsState = {
  forumActive: null,
  forumMensagemEditing: {},
  selectedForumMensagem: null,
  foruns: [],
  forunsCount: 0,
  mensagensPendentes: [],
  mensagensPendentesCount: 0,
  request: {
    errorCode: null,
    message: '',
    fetching: false,
  },
  queryForuns: { limit: 10, offset: 0 },
  queryMensagensPendentes: { limit: 10, offset: 0 },
}

const forunsStore = createSimpleStore(
  initialState,
  {
    requestAsync(state) {
      state.request = {
        ...initialState.request,
        fetching: true,
      }
    },
    requestError(state, payload: { errorCode: number; message: string }) {
      state.request = {
        errorCode: payload.errorCode,
        message: payload.message,
        fetching: false,
      }
    },
    setEditaForumMensagem(state, payload: { forumMensagem: Partial<ForumMensagemModel> }) {
      if (!payload.forumMensagem) {
        state.forumMensagemEditing = {}
      } else {
        state.forumMensagemEditing = payload.forumMensagem
      }
    },
    setMensagemResposta(state, payload: { texto: string }) {
      state.forumMensagemEditing = { ...state.forumMensagemEditing, texto: payload.texto }
    },
    setSelectedForumMensagem(state, payload: { selectedForumMensagem: Partial<ForumMensagemModel> }) {
      state.selectedForumMensagem = payload.selectedForumMensagem
    },
    forumActiveSet(state, payload: { forum: Forum }) {
      state.forumActive = payload.forum
    },
    forumActiveClean(state) {
      state.forumActive = null
    },
    forunsGetSuccess(state, payload: { rows: Forum[]; count: number }) {
      state.foruns = payload.rows
      state.forunsCount = payload.count
      state.request = initialState.request
    },
    mensagensPendenteGetSuccess(state, payload: { rows: Forum[]; count: number }) {
      state.mensagensPendentes = payload.rows
      state.mensagensPendentesCount = payload.count
      state.request = initialState.request
    },
    setQueryParamsForuns(state, payload: IQueryParams) {
      state.queryForuns = { ...state.queryForuns, ...payload }
    },
    setQueryParamsMensagensPendentes(state, payload: IQueryParams) {
      state.queryMensagensPendentes = { ...state.queryMensagensPendentes, ...payload }
    },
  },
  {
    thunks: {
      paginateForuns: (query: IQueryParams) => (dispatch, getState) => {
        dispatch(forunsStore.actions.setQueryParamsForuns(query))
        forunsStore.thunks.getForuns()(dispatch, getState)
      },
      paginateMensagensPendentes: (query: IQueryParams) => (dispatch, getState) => {
        dispatch(forunsStore.actions.setQueryParamsMensagensPendentes(query))
        forunsStore.thunks.getMensagensPendentes()(dispatch, getState)
      },
      enviarMensagemResposta: () => async (dispatch, getState) => {
        try {
          const forumMensagemEditing = getState().forumMensagemEditing
          await api.ForunsMensagens.createOrUpdate({
            ...forumMensagemEditing,
            forumId: getState().forumActive.id,
          } as ForumMensagemModel)
          await forunsStore.thunks.setForum(getState().forumActive.id)(dispatch)
          dispatch(forunsStore.actions.setEditaForumMensagem({ forumMensagem: new ForumMensagemModel() }))
        } catch (error) {
          // Error handling
        }
      },
      aprovarMensagem: (forumMensagem: ForumMensagemModel) => async (dispatch, getState) => {
        try {
          await api.Foruns.aprovarMensagem(forumMensagem.id)
          await forunsStore.thunks.getMensagensPendentes()(dispatch, getState)
          await forunsStore.thunks.setForum(forumMensagem.forumId)(dispatch)
        } catch (error) {
          console.log({ error })
        }
      },
      rejeitarMensagem: (forumMensagem: ForumMensagemModel) => async (dispatch, getState) => {
        try {
          await api.Foruns.rejeitar(forumMensagem.id)
          await forunsStore.thunks.getMensagensPendentes()(dispatch, getState)
          await forunsStore.thunks.setForum(forumMensagem.forumId)(dispatch)
        } catch (error) {
          // Error handling
        }
      },
      aprovarForum: () => async (dispatch, getState) => {
        try {
          await api.Foruns.aprovarForum(getState().forumActive.id)
          await forunsStore.thunks.getForuns()(dispatch, getState)
          await forunsStore.thunks.setForum(null)(dispatch)
        } catch (error) {
          console.log({ error })
        }
      },
      setForum: (forumId: number) => async (dispatch) => {
        if (!forumId) {
          dispatch(forunsStore.actions.forumActiveClean())
          dispatch(forunsStore.actions.setSelectedForumMensagem({ selectedForumMensagem: null }))
          return
        }
        try {
          const [forum, forunsMensagens] = await Promise.all([
            api.Foruns.getOne(forumId),
            api.Foruns.getForunsMensagensByForum(forumId),
          ])
          dispatch(forunsStore.actions.forumActiveSet({ forum: { ...forum, forunsMensagens, textoPendente: '' } as Forum }))
        } catch (e) {
          dispatch(forunsStore.actions.requestError({ errorCode: 400, message: 'falha ao consultar foruns' }))
        }
      },
      setForumMensagem: (forumMensagem: ForumMensagemModel) => async (dispatch, getState) => {
        dispatch(forunsStore.actions.setSelectedForumMensagem({ selectedForumMensagem: forumMensagem }))
        await forunsStore.thunks.setForum(forumMensagem.forumId)(dispatch)
        return true
      },
      getForuns: () => async (dispatch, getState) => {
        dispatch(forunsStore.actions.requestAsync())
        try {
          const forunsResponse = await api.Foruns.getAll(getState().queryForuns)
          const foruns = {
            ...forunsResponse,
            rows: forunsResponse.rows.map((f: ForumModel) => ({ ...f, textoPendente: '' } as Forum))
          }
          dispatch(forunsStore.actions.forunsGetSuccess(foruns))
        } catch (error) {
          dispatch(forunsStore.actions.requestError({ errorCode: 400, message: 'Falha a consultar foruns' }))
        }
      },
      getMensagensPendentes: () => async (dispatch, getState) => {
        dispatch(forunsStore.actions.requestAsync())
        try {
          const mensagensResponse = await api.Foruns.getForunsMensagensPendentes(getState().queryMensagensPendentes)
          // Convert ForumMensagemModel[] to Forum[] - this seems like a type mismatch in the store design
          const foruns = {
            count: mensagensResponse.count,
            rows: mensagensResponse.rows.map((msg: ForumMensagemModel) => ({
              id: msg.forumId,
              titulo: '',
              texto: msg.texto,
              textoPendente: msg.textoPendente || '',
              pontos: 0,
              usuarioId: msg.usuarioId,
              usuario: msg.usuario,
              aguardandoModeracao: true,
              forunsMensagens: [msg],
              createdAt: msg.createdAt,
              updatedAt: msg.updatedAt
            } as Forum))
          }
          dispatch(forunsStore.actions.mensagensPendenteGetSuccess(foruns))
        } catch (error) {
          dispatch(forunsStore.actions.requestError({ errorCode: 400, message: 'Falha a consultar mensagens' }))
        }
      },
    },
  }
)

export { forunsStore }
export const { Provider: ForunsProvider, useDispatch: useForunsDispatch, useState: useForunsState, useSelector: useForunsSelector } = forunsStore